/**
 * src/lib/events.ts — 2304vc
 * Event and bin data functions
 */

import { supabase } from './supabase'

export interface VenueEvent {
  id:              string
  venue_id:        string
  name:            string
  event_date:      string
  type:            'pro' | 'floor'
  status:          'setup' | 'active' | 'closed'
  onedrive_folder: string | null
  created_at:      string
}

export interface VenueBin {
  id:       string
  venue_id: string
  name:     string
  color:    string
  position: number
}

export async function fetchEvents(venueId: string): Promise<VenueEvent[]> {
  const { data, error } = await supabase
    .from('club_events')
    .select('*')
    .eq('venue_id', venueId)
    .order('event_date', { ascending: false })
  if (error) throw error
  return data ?? []
}

export async function fetchActiveEvent(venueId: string): Promise<VenueEvent | null> {
  const { data, error } = await supabase
    .from('club_events')
    .select('*')
    .eq('venue_id', venueId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .single()
  if (error) return null
  return data
}

export async function createEvent(
  venueId: string,
  name: string,
  type: 'pro' | 'floor',
  date: string
): Promise<VenueEvent> {
  const { data, error } = await supabase
    .from('club_events')
    .insert({ venue_id: venueId, name, type, event_date: date, status: 'active' })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function closeEvent(eventId: string): Promise<void> {
  await supabase.from('club_events').update({ status: 'closed' }).eq('id', eventId)
}

export async function fetchBins(venueId: string): Promise<VenueBin[]> {
  const { data, error } = await supabase
    .from('bins')
    .select('*')
    .eq('venue_id', venueId)
    .order('position')
  if (error) throw error
  return data ?? []
}

export async function createBin(venueId: string, name: string, color: string): Promise<VenueBin> {
  const { data: existing } = await supabase
    .from('bins')
    .select('position')
    .eq('venue_id', venueId)
    .order('position', { ascending: false })
    .limit(1)
    .single()
  const position = (existing?.position ?? -1) + 1
  const { data, error } = await supabase
    .from('bins')
    .insert({ venue_id: venueId, name, color, position })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteBin(binId: string): Promise<void> {
  await supabase.from('bins').delete().eq('id', binId)
}

export async function renameBin(binId: string, name: string): Promise<void> {
  await supabase.from('bins').update({ name }).eq('id', binId)
}

// Default event name from date + venue
export function defaultEventName(venueName: string, type: 'pro' | 'floor'): string {
  const d = new Date()
  const day   = d.getDate().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const year  = d.getFullYear()
  const label = type === 'floor' ? 'Floor' : 'Pro'
  return `${venueName} ${day}/${month}/${year} ${label}`
}
